import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth'
import { auth } from '../lib/firebase'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import logo from '../assets/wonderstood-high-resolution-logo-grayscale-transparent.png'

export default function PasswordReset() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [verifyingCode, setVerifyingCode] = useState(true)
  const [email, setEmail] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const oobCode = searchParams.get('oobCode')

  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode) {
        setError('Invalid or missing reset code')
        setVerifyingCode(false)
        return
      }

      try {
        // Verify the reset code is valid and get the email
        const userEmail = await verifyPasswordResetCode(auth, oobCode)
        setEmail(userEmail)
        setVerifyingCode(false)
      } catch (error: any) {
        console.error('Error verifying reset code:', error)
        if (error.code === 'auth/invalid-action-code') {
          setError('This password reset link is invalid or has expired. Please request a new one.')
        } else if (error.code === 'auth/expired-action-code') {
          setError('This password reset link has expired. Please request a new one.')
        } else {
          setError('Failed to verify reset code. Please try requesting a new password reset link.')
        }
        setVerifyingCode(false)
      }
    }

    verifyCode()
  }, [oobCode])

  const sanitizeInput = (value: string): string => {
    let sanitized = value.replace(/[\x00-\x1F\x7F]/g, '')
    sanitized = sanitized.replace(/<[^>]*>/g, '')
    sanitized = sanitized.replace(/&[#\w]+;/g, '')
    sanitized = sanitized.trim()
    sanitized = sanitized.slice(0, 128)

    const dangerousPatterns = [
      /javascript:/i,
      /<script/i,
      /onload=/i,
      /onerror=/i,
      /onclick=/i,
      /eval\(/i,
      /expression\(/i
    ]

    for (const pattern of dangerousPatterns) {
      if (pattern.test(sanitized)) {
        return ''
      }
    }

    return sanitized
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      // Validate password
      if (!password || password.length === 0) {
        throw new Error('Password is required')
      }

      if (password.length < 8) {
        throw new Error('Password must be at least 8 characters long')
      }

      if (password.length > 128) {
        throw new Error('Password must be less than 128 characters')
      }

      // Check if passwords match
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match')
      }

      if (!oobCode) {
        throw new Error('Invalid reset code')
      }

      // Reset the password
      await confirmPasswordReset(auth, oobCode, password)
      setSuccess(true)

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/?signin=true')
      }, 3000)
    } catch (error: any) {
      console.error('Password reset error:', error)

      if (error.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.')
      } else if (error.code === 'auth/invalid-action-code') {
        setError('This password reset link is invalid or has already been used.')
      } else if (error.code === 'auth/expired-action-code') {
        setError('This password reset link has expired. Please request a new one.')
      } else {
        setError(error.message || 'Failed to reset password')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={logo} alt="Wonderstood" className="h-12 w-auto mx-auto mb-4" />
          <h2 className="text-3xl font-semibold text-gray-900">Reset your password</h2>
          {email && !verifyingCode && !error && (
            <p className="mt-2 text-sm text-gray-600">
              for <span className="font-medium text-gray-900">{email}</span>
            </p>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {verifyingCode ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-sm text-gray-600">Verifying reset link...</p>
            </div>
          ) : error && !email ? (
            <div>
              <div className="rounded-md bg-red-50 p-4 mb-6 border border-red-200">
                <p className="text-sm text-red-800">{error}</p>
              </div>
              <button
                onClick={() => navigate('/?signin=true')}
                className="w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                Back to Sign In
              </button>
            </div>
          ) : success ? (
            <div className="text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-gray-900">
                Password reset successful!
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Your password has been updated. You can now sign in with your new password.
              </p>
              <p className="mt-4 text-sm text-gray-500">
                Redirecting to sign in...
              </p>
            </div>
          ) : (
            <div>
              {error && (
                <div className="mb-4 rounded-md bg-red-50 p-4 border border-red-200">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-900">
                    New password
                  </label>
                  <div className="mt-2 relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      required
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(sanitizeInput(e.target.value))}
                      minLength={8}
                      maxLength={128}
                      className="block w-full rounded-md bg-white px-3 py-1.5 pr-10 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      placeholder="Enter new password (min 8 characters)"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Must be at least 8 characters long
                  </p>
                </div>

                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-900">
                    Confirm new password
                  </label>
                  <div className="mt-2 relative">
                    <input
                      id="confirm-password"
                      name="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(sanitizeInput(e.target.value))}
                      minLength={8}
                      maxLength={128}
                      className="block w-full rounded-md bg-white px-3 py-1.5 pr-10 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-indigo-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Resetting password...' : 'Reset password'}
                </button>
              </form>
            </div>
          )}
        </div>

        {!verifyingCode && !success && (
          <p className="mt-4 text-center text-sm text-gray-600">
            Remember your password?{' '}
            <button
              onClick={() => navigate('/?signin=true')}
              className="font-semibold text-indigo-600 hover:text-indigo-500"
            >
              Sign in
            </button>
          </p>
        )}
      </div>
    </div>
  )
}
